import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

const CarSvg = ({ w = 56 }) => (
  <g>
    <rect x="0" y="8" width="52" height="16" rx="8" fill="#1DAF5A" />
    <rect x="12" y="0" width="26" height="14" rx="6" fill="#22C55E" />
    <rect x="15" y="3" width="9" height="8" rx="2" fill="#0B1210" />
    <rect x="27" y="3" width="9" height="8" rx="2" fill="#0B1210" />
    <circle cx="12" cy="26" r="6" fill="#0B1210" stroke="#3A4046" strokeWidth="2" />
    <circle cx="40" cy="26" r="6" fill="#0B1210" stroke="#3A4046" strokeWidth="2" />
    <rect x="50" y="10" width="6" height="4" rx="2" fill="#F9C31F" />
  </g>
);

const ROAD = "M-60,330 C140,250 340,390 660,290";

export const CarScene = ({ compact = false }) => (
  <div className="absolute inset-0 overflow-hidden" data-testid="car-scene">
    <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#1DAF5A]/15 blur-3xl" />
    <div className="absolute bottom-0 -left-16 w-64 h-64 rounded-full bg-[#F9C31F]/10 blur-3xl" />
    <svg viewBox="0 0 600 400" className="absolute inset-0 w-full h-full"
      preserveAspectRatio={compact ? "xMidYMid slice" : "xMidYMax slice"}>
      <path d={ROAD} stroke="#23282D" strokeWidth="44" fill="none" strokeLinecap="round" />
      <path d={ROAD} stroke="#F9C31F" strokeWidth="3" strokeDasharray="16 20" fill="none" className="road-dash" opacity="0.8" />
      <circle cx="140" cy="284" r="6" fill="#1DAF5A" className="animate-pulse" />
      <circle cx="470" cy="352" r="6" fill="#F9C31F" className="animate-pulse" />
      <g>
        <animateMotion dur="10s" repeatCount="indefinite" rotate="auto" path={ROAD} />
        <g transform="translate(-26,-16)"><CarSvg /></g>
      </g>
    </svg>
  </div>
);

export const RoadStrip = () => (
  <div className="relative h-24 overflow-hidden bg-[#090A0B]" data-testid="road-strip">
    <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-10 bg-[#23282D]" />
    <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[3px] road-lane" />
    <div className="car-run absolute top-1/2 -translate-y-[26px]">
      <svg width="56" height="32" viewBox="0 0 56 32"><CarSvg /></svg>
    </div>
  </div>
);

export const CarpoolScene = () => {
  const P = "M20,240 C160,140 300,300 440,180 S560,120 590,110";
  const PASSENGERS = ["#F9C31F", "#4ADE80", "#60A5FA"];
  return (
    <div className="relative w-full max-w-3xl mx-auto" data-testid="carpool-scene">
      <svg viewBox="0 0 620 300" className="w-full h-auto">
        <path d={P} stroke="#23282D" strokeWidth="30" fill="none" strokeLinecap="round" />
        <path d={P} stroke="#F9C31F" strokeWidth="2.5" strokeDasharray="12 16" fill="none" className="road-dash" opacity="0.9" />
        {[[120, 176], [330, 234], [470, 160]].map(([x, y], i) => (
          <g key={`${x}-${y}`}>
            <circle cx={x} cy={y} r="14" fill="#1DAF5A" opacity="0.25">
              <animate attributeName="r" values="10;22;10" dur="2.4s" begin={`${i * 0.6}s`} repeatCount="indefinite" />
            </circle>
            <circle cx={x} cy={y} r="7" fill="#22C55E" />
            <g>
              <circle cx={x} cy={y - 28} r="9" fill={PASSENGERS[i]} />
              <rect x={x - 7} y={y - 20} width="14" height="12" rx="6" fill={PASSENGERS[i]} opacity="0.85" />
              <animateTransform attributeName="transform" type="translate" values="0,0; 0,-6; 0,0"
                dur="1.6s" begin={`${i * 0.4}s`} repeatCount="indefinite" />
            </g>
          </g>
        ))}
        <g transform="translate(586,64)">
          <rect x="0" y="0" width="3" height="46" rx="1.5" fill="#4B5563" />
          <path d="M3,2 L30,9 L3,17 Z" fill="#22C55E">
            <animateTransform attributeName="transform" type="skewY" values="0;6;0" dur="2s" repeatCount="indefinite" />
          </path>
        </g>
        <g>
          <animateMotion dur="8s" repeatCount="indefinite" rotate="auto" path={P} />
          <g transform="translate(-36,-24) scale(1.35)">
            <rect x="0" y="10" width="54" height="16" rx="8" fill="#1DAF5A" />
            <rect x="9" y="0" width="34" height="16" rx="7" fill="#22C55E" />
            {PASSENGERS.map((c, i) => (
              <circle key={c} cx={15 + i * 11} cy={7} r="4" fill={c} />
            ))}
            <circle cx="12" cy="28" r="6" fill="#0B1210" stroke="#3A4046" strokeWidth="2" />
            <circle cx="42" cy="28" r="6" fill="#0B1210" stroke="#3A4046" strokeWidth="2" />
            <rect x="52" y="13" width="6" height="4" rx="2" fill="#F9C31F" />
          </g>
        </g>
      </svg>
      <div className="absolute top-2 left-2 glass rounded-full px-4 py-1.5 text-xs font-bold text-[#12211C] animate-pulse" data-testid="carpool-chip-1">
        🟢 Priya joined · seat 2/4
      </div>
      <div className="absolute bottom-2 right-2 glass rounded-full px-4 py-1.5 text-xs font-bold text-[#12211C] animate-pulse" data-testid="carpool-chip-2">
        95% match · ₹40/seat
      </div>
    </div>
  );
};

export const AuthShell = ({ children, tagline }) => (
  <div className="min-h-screen bg-[#090A0B] text-white flex flex-col">
    <div className="relative h-40 md:h-48 overflow-hidden border-b border-white/10">
      <div className="absolute -top-16 right-10 w-64 h-64 rounded-full bg-[#1DAF5A]/15 blur-3xl" />
      <div className="absolute inset-x-0 bottom-5 h-10 bg-[#23282D]" />
      <div className="absolute inset-x-0 bottom-[38px] h-[3px] road-lane" />
      <div className="car-run absolute bottom-[46px]" data-testid="car-scene">
        <svg width="56" height="32" viewBox="0 0 56 32"><CarSvg /></svg>
      </div>
      <div className="absolute inset-0 z-10 flex flex-col justify-start pt-6 px-6 md:px-10 pointer-events-none">
        <Link to="/" data-testid="auth-logo" className="pointer-events-auto w-fit font-display font-black text-3xl text-[#22C55E]">THIRI</Link>
        <p className="mt-1 text-white/50 text-sm max-w-sm">{tagline}</p>
      </div>
    </div>
    <div className="flex-1 flex items-start md:items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">{children}</div>
    </div>
    <div className="pb-8 flex items-center justify-center gap-2 text-white/40 text-xs font-bold uppercase tracking-[0.2em]">
      <ShieldCheck className="w-4 h-4 text-[#22C55E]" /> Verified campus network
    </div>
  </div>
);
